import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type RefObject,
} from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';

import { Button } from '@/components/Button/Button';
import type { ApiError } from '@/api/client.types';

import type { EnrollmentCourse } from './courseEnrollmentService';
import { useAddCourseEnrollment } from './useAddCourseEnrollment';
import {
  getCoursePaymentStripePromise,
  getStripePublishableKey,
} from './stripeConfig';
import styles from './AddCourseDialog.module.css';

type DialogState =
  | { kind: 'idle'; message?: string }
  | { kind: 'searching'; normalizedCourseId: string; requestId: number }
  | { kind: 'notFound'; normalizedCourseId: string }
  | { kind: 'alreadyEnrolled'; normalizedCourseId: string }
  | { kind: 'lookupError'; message: string; normalizedCourseId: string }
  | { course: EnrollmentCourse; kind: 'found'; message?: string }
  | { course: EnrollmentCourse; kind: 'codeEntry'; message?: string }
  | { course: EnrollmentCourse; kind: 'redeemingCode' }
  | { course: EnrollmentCourse; kind: 'initiatingPayment' }
  | { clientSecret: string; course: EnrollmentCourse; kind: 'paymentReady' }
  | { course: EnrollmentCourse; kind: 'enrolling'; source: 'free' | 'paid' }
  | { course: EnrollmentCourse; kind: 'success' }
  | {
      course: EnrollmentCourse;
      kind: 'enrollmentError';
      message: string;
      paymentCompleted: boolean;
    };

interface AddCourseDialogProps {
  enrolledCourseIds: string[];
  // Where focus goes on close if what opened the dialog has gone.
  fallbackFocusRef?: RefObject<HTMLElement | null>;
  // Called once the dialog has closed on a course just joined, so what the
  // page says then reaches a screen reader.
  onCourseAdded?(courseName: string): void;
  onOpenChange(open: boolean): void;
  open: boolean;
}

function normalizeCourseId(value: string) {
  return value.trim().toLowerCase();
}

function formatCourseFee(centAmount: number) {
  if (centAmount === 0) {
    return 'Free';
  }

  return new Intl.NumberFormat('en-US', {
    currency: 'USD',
    style: 'currency',
  }).format(centAmount / 100);
}

function isNotFoundError(error: unknown) {
  return (
    typeof error === 'object' &&
    error !== null &&
    'kind' in error &&
    (error as ApiError).kind === 'data' &&
    (error as ApiError).status === 404
  );
}

function getLookupErrorMessage(error: unknown) {
  if (isNotFoundError(error)) {
    return null;
  }

  return "Couldn't look up that course right now. Please try again.";
}

function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}

function isProtectedState(state: DialogState) {
  return (
    state.kind === 'enrolling' ||
    (state.kind === 'enrollmentError' && state.paymentCompleted)
  );
}

function CourseSummary({ course }: { course: EnrollmentCourse }) {
  return (
    <section aria-label="Course details" className={styles.courseSummary}>
      <h3 className={styles.courseName}>{course.name}</h3>
      <dl className={styles.details}>
        <div className={styles.detailRow}>
          <dt className={styles.detailLabel}>Course ID</dt>
          <dd className={styles.detailValue}>{course.id}</dd>
        </div>
        <div className={styles.detailRow}>
          <dt className={styles.detailLabel}>Fee</dt>
          <dd className={styles.detailValue}>
            {formatCourseFee(course.premiumFee)}
          </dd>
        </div>
      </dl>
    </section>
  );
}

export function AddCourseDialog({
  enrolledCourseIds,
  fallbackFocusRef,
  onCourseAdded,
  onOpenChange,
  open,
}: AddCourseDialogProps) {
  const courseIdInputId = useId();
  const accessCodeInputId = useId();
  const latestRequestIdRef = useRef(0);
  const closeTimerRef = useRef<number | null>(null);
  const courseIdInputRef = useRef<HTMLInputElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const joinedCourseRef = useRef<EnrollmentCourse | null>(null);
  const [courseIdInput, setCourseIdInput] = useState('');
  const [accessCodeInput, setAccessCodeInput] = useState('');
  const [state, setState] = useState<DialogState>({ kind: 'idle' });
  const {
    addCourseToUser,
    lookupCourseForEnrollment,
    redeemAccessCode,
    refreshAfterEnrollment,
    startStripeCoursePayment,
  } = useAddCourseEnrollment();
  const enrolledCourseIdSet = useMemo(
    () =>
      new Set(
        enrolledCourseIds.map((courseId) => normalizeCourseId(courseId)),
      ),
    [enrolledCourseIds],
  );
  const isSearching = state.kind === 'searching';
  const isBusy =
    state.kind === 'searching' ||
    state.kind === 'initiatingPayment' ||
    state.kind === 'redeemingCode' ||
    state.kind === 'enrolling';
  const canDismiss = !isProtectedState(state);

  const resetDialog = useCallback(() => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    latestRequestIdRef.current += 1;
    setCourseIdInput('');
    setAccessCodeInput('');
    setState({ kind: 'idle' });
  }, []);

  useEffect(() => {
    if (!open) {
      resetDialog();
    }
  }, [open, resetDialog]);

  // Kept for when the dialog has closed: closing resets the state.
  useEffect(() => {
    if (state.kind === 'success') {
      joinedCourseRef.current = state.course;
    }
  }, [state]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    },
    [],
  );

  const completeEnrollment = useCallback(
    async (
      course: EnrollmentCourse,
      source: 'free' | 'paid',
      paymentCompleted: boolean,
    ) => {
      setState({ course, kind: 'enrolling', source });

      try {
        await addCourseToUser(course.id);
        await refreshAfterEnrollment();
        setState({ course, kind: 'success' });
        closeTimerRef.current = window.setTimeout(() => {
          onOpenChange(false);
          resetDialog();
        }, 900);
      } catch {
        setState({
          course,
          kind: 'enrollmentError',
          message: paymentCompleted
            ? "Payment received but enrollment failed. Don't worry - your payment is recorded."
            : "Couldn't add this course. Please try again.",
          paymentCompleted,
        });
      }
    },
    [addCourseToUser, onOpenChange, refreshAfterEnrollment, resetDialog],
  );

  const handlePaymentComplete = useCallback(
    (course: EnrollmentCourse) => {
      void completeEnrollment(course, 'paid', true);
    },
    [completeEnrollment],
  );

  const handleSubmitLookup = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const normalizedCourseId = normalizeCourseId(courseIdInput);

      if (!normalizedCourseId) {
        setState({ kind: 'idle', message: 'Enter a course ID.' });
        return;
      }

      if (enrolledCourseIdSet.has(normalizedCourseId)) {
        setState({ kind: 'alreadyEnrolled', normalizedCourseId });
        return;
      }

      const requestId = latestRequestIdRef.current + 1;
      latestRequestIdRef.current = requestId;
      setState({ kind: 'searching', normalizedCourseId, requestId });

      try {
        const course = await lookupCourseForEnrollment(normalizedCourseId);

        if (latestRequestIdRef.current !== requestId) {
          return;
        }

        setState({ course, kind: 'found' });
      } catch (error) {
        if (latestRequestIdRef.current !== requestId) {
          return;
        }

        const lookupMessage = getLookupErrorMessage(error);
        setState(
          lookupMessage
            ? {
                kind: 'lookupError',
                message: lookupMessage,
                normalizedCourseId,
              }
            : { kind: 'notFound', normalizedCourseId },
        );
      }
    },
    [courseIdInput, enrolledCourseIdSet, lookupCourseForEnrollment],
  );

  const handlePayWithCard = useCallback(
    async (course: EnrollmentCourse) => {
      if (!getStripePublishableKey()) {
        setState({
          course,
          kind: 'found',
          message: 'Card payment is not configured yet. Please contact support.',
        });
        return;
      }

      setState({ course, kind: 'initiatingPayment' });

      try {
        const clientSecret = await startStripeCoursePayment(course.premiumFee);
        setState({ clientSecret, course, kind: 'paymentReady' });
      } catch {
        setState({
          course,
          kind: 'found',
          message: "Couldn't start card payment. Please try again.",
        });
      }
    },
    [startStripeCoursePayment],
  );

  const handlePayWithCode = useCallback((course: EnrollmentCourse) => {
    setAccessCodeInput('');
    setState({ course, kind: 'codeEntry' });
  }, []);

  const handleRedeemCode = useCallback(
    async (course: EnrollmentCourse, event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const code = accessCodeInput.trim();

      if (!code) {
        setState({
          course,
          kind: 'codeEntry',
          message: 'Enter an access code.',
        });
        return;
      }

      setState({ course, kind: 'redeemingCode' });

      try {
        await redeemAccessCode({
          code,
          courseId: course.id,
          valueCents: course.premiumFee,
        });
      } catch {
        setState({
          course,
          kind: 'codeEntry',
          message:
            "That access code couldn't be redeemed. Check the code and try again.",
        });
        return;
      }

      await completeEnrollment(course, 'paid', true);
    },
    [accessCodeInput, completeEnrollment, redeemAccessCode],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && !canDismiss) {
        return;
      }

      onOpenChange(nextOpen);

      if (!nextOpen) {
        resetDialog();
      }
    },
    [canDismiss, onOpenChange, resetDialog],
  );

  function handleCourseIdChange(value: string) {
    setCourseIdInput(value);

    if (
      state.kind !== 'idle' &&
      state.kind !== 'searching' &&
      !isProtectedState(state)
    ) {
      setState({ kind: 'idle' });
    }
  }

  const statusMessage =
    state.kind === 'searching'
      ? `Looking up ${state.normalizedCourseId}.`
      : state.kind === 'initiatingPayment'
        ? 'Starting secure card payment.'
        : state.kind === 'redeemingCode'
          ? 'Redeeming access code.'
          : state.kind === 'enrolling'
          ? 'Adding course to your account.'
          : '';

  const paymentOptions = useMemo(
    () =>
      state.kind === 'paymentReady'
        ? {
            clientSecret: state.clientSecret,
            onComplete: () => handlePaymentComplete(state.course),
          }
        : null,
    [handlePaymentComplete, state],
  );

  return (
    <Dialog.Root onOpenChange={handleOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          data-payment-ready={state.kind === 'paymentReady' ? 'true' : 'false'}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            // Gone after a join from the empty state: the list replaced it.
            const returnTo = returnFocusRef.current?.isConnected
              ? returnFocusRef.current
              : fallbackFocusRef?.current;
            returnTo?.focus();

            if (joinedCourseRef.current) {
              onCourseAdded?.(joinedCourseRef.current.name);
              joinedCourseRef.current = null;
            }
          }}
          onOpenAutoFocus={(event) => {
            // Focus has not moved yet: this is what opened the dialog. A browser
            // that does not focus a clicked button leaves it on the page.
            const { activeElement, body } = document;
            returnFocusRef.current =
              activeElement instanceof HTMLElement && activeElement !== body
                ? activeElement
                : null;
            event.preventDefault();
            courseIdInputRef.current?.focus();
          }}
          onEscapeKeyDown={(event) => {
            if (!canDismiss) {
              event.preventDefault();
            }
          }}
          onInteractOutside={(event) => {
            if (!canDismiss) {
              event.preventDefault();
            }
          }}
        >
          <div className={styles.header}>
            <div>
              <Dialog.Title className={styles.title}>Add Course</Dialog.Title>
              <Dialog.Description asChild>
                <p className={styles.description}>
                  Enter the course ID from your instructor to sign up.
                </p>
              </Dialog.Description>
            </div>
          </div>

          <div aria-atomic="true" aria-live="polite" className={styles.srOnly}>
            {statusMessage}
          </div>

          <div className={styles.body}>
            <form className={styles.lookupRow} onSubmit={handleSubmitLookup}>
              <label className={styles.field} htmlFor={courseIdInputId}>
                <span className={styles.label}>Enter course ID</span>
                <input
                  autoComplete="off"
                  className={styles.input}
                  disabled={isBusy || state.kind === 'paymentReady'}
                  id={courseIdInputId}
                  onChange={(event) =>
                    handleCourseIdChange(event.target.value)
                  }
                  ref={courseIdInputRef}
                  value={courseIdInput}
                />
              </label>
              <Button disabled={isBusy} type="submit">
                {isSearching ? 'Searching...' : 'Search'}
              </Button>
            </form>

            {state.kind === 'idle' && state.message ? (
              <p className={styles.error} role="alert">
                {state.message}
              </p>
            ) : null}

            {state.kind === 'alreadyEnrolled' ? (
              <p className={styles.error} role="alert">
                You're already enrolled in this course.
              </p>
            ) : null}

            {state.kind === 'notFound' ? (
              <p className={styles.error} role="alert">
                We couldn't find a course with that ID. Check the ID and try
                again.
              </p>
            ) : null}

            {state.kind === 'lookupError' ? (
              <p className={styles.error} role="alert">
                {state.message}
              </p>
            ) : null}

            {(state.kind === 'found' ||
              state.kind === 'codeEntry' ||
              state.kind === 'redeemingCode' ||
              state.kind === 'initiatingPayment' ||
              state.kind === 'paymentReady' ||
              state.kind === 'enrolling' ||
              state.kind === 'success' ||
              state.kind === 'enrollmentError') ? (
              <CourseSummary course={state.course} />
            ) : null}

            {state.kind === 'found' && state.message ? (
              <p className={styles.error} role="alert">
                {state.message}
              </p>
            ) : null}

            {state.kind === 'codeEntry' || state.kind === 'redeemingCode' ? (
              <form
                className={styles.lookupRow}
                onSubmit={(event) => void handleRedeemCode(state.course, event)}
              >
                <label className={styles.field} htmlFor={accessCodeInputId}>
                  <span className={styles.label}>Access code</span>
                  <input
                    autoComplete="off"
                    autoFocus
                    className={styles.input}
                    disabled={state.kind === 'redeemingCode'}
                    id={accessCodeInputId}
                    onChange={(event) => setAccessCodeInput(event.target.value)}
                    value={accessCodeInput}
                  />
                </label>
                <Button
                  disabled={state.kind === 'redeemingCode'}
                  type="submit"
                >
                  {state.kind === 'redeemingCode'
                    ? 'Redeeming...'
                    : 'Redeem code'}
                </Button>
              </form>
            ) : null}

            {state.kind === 'codeEntry' && state.message ? (
              <p className={styles.error} role="alert">
                {state.message}
              </p>
            ) : null}

            {state.kind === 'success' ? (
              <p className={styles.success}>
                {state.course.name} has been added to your courses.
              </p>
            ) : null}

            {state.kind === 'enrollmentError' ? (
              <div
                className={classNames(
                  state.paymentCompleted ? styles.warning : styles.error,
                )}
                role="alert"
              >
                <p>{state.message}</p>
                {state.paymentCompleted ? (
                  <p className={styles.supportText}>
                    Retry enrollment below. If it still fails, email
                    support@kudu.com and include the course ID {state.course.id}.
                  </p>
                ) : null}
              </div>
            ) : null}

            {state.kind === 'paymentReady' && paymentOptions ? (
              <div className={styles.paymentFrame}>
                <EmbeddedCheckoutProvider
                  options={paymentOptions}
                  stripe={getCoursePaymentStripePromise()}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </div>
            ) : null}
          </div>

          <div className={styles.footer}>
            {canDismiss ? (
              <Button onClick={() => handleOpenChange(false)} variant="secondary">
                Cancel
              </Button>
            ) : null}

            {state.kind === 'found' && state.course.premiumFee === 0 ? (
              <Button
                onClick={() =>
                  void completeEnrollment(state.course, 'free', false)
                }
              >
                Sign Up
              </Button>
            ) : null}

            {state.kind === 'found' && state.course.premiumFee > 0 ? (
              <>
                <Button
                  onClick={() => handlePayWithCode(state.course)}
                  variant="secondary"
                >
                  Pay with Code
                </Button>
                <Button onClick={() => void handlePayWithCard(state.course)}>
                  Pay with Card
                </Button>
              </>
            ) : null}

            {state.kind === 'initiatingPayment' ? (
              <Button disabled>Starting payment...</Button>
            ) : null}

            {state.kind === 'enrolling' ? (
              <Button disabled>Adding course...</Button>
            ) : null}

            {state.kind === 'enrollmentError' ? (
              <Button
                onClick={() =>
                  void completeEnrollment(
                    state.course,
                    state.paymentCompleted ? 'paid' : 'free',
                    state.paymentCompleted,
                  )
                }
              >
                Retry Enrollment
              </Button>
            ) : null}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
